import {
  Callout,
  Checkbox,
  DefaultButton,
  DirectionalHint,
  IconButton, IStyle,
  SearchBox,
  Separator,
  Spinner,
  Stack,
  TextField
} from "office-ui-fabric-react";
import React, { useEffect, useRef, useState, VFC } from "react";
import { unstable_batchedUpdates } from "react-dom";
import styled from "styled-components";
import FiltersList from "./FiltersList";
import { theme } from "theme";
import PrimaryButton from "../UI/PrimaryButton";

const LoaderContainer = styled.div`
  display: flex;
  height: 100px;
  justify-content: center;
  alignt-items: center;
`;

const EmptyFilter = styled.div`
  display: flex;
  height: 100px;
  justify-content: center;
  align-items: center;
`;

interface IProps {
  showFilter: boolean;
  isLoading: boolean;
  filteredList: any[];
  onShowFilter: any;
  onFilterClose: any;
  acceptFilter: (field: string, value: any) => void;
  field: string;
  isFiltered: boolean;
  onLoadMore: (limit: number, offset: number) => void;
  canLoadMore: boolean;
  useTextBlock?: boolean
  textBlockLabel?: string;
  emptyArrayOnAllChecked?: boolean;
  defaultSelectedValues?: any[];
  displayAllInsteadOfElements?: boolean;
  canAcceptEmpty?: boolean;
  hideSearch?: boolean;
  styles?: Partial<{
    textInput: IStyle;
  }>;
}

const FilterCallout: VFC<IProps> = (props) => {
    const {
            showFilter,
            isLoading,
            filteredList,
            onShowFilter,
            onFilterClose,
            acceptFilter,
            field,
            isFiltered,
            onLoadMore,
            canLoadMore,
            useTextBlock,
            textBlockLabel,
            emptyArrayOnAllChecked      = true,
            defaultSelectedValues,
            displayAllInsteadOfElements = true,
            canAcceptEmpty,
            hideSearch,
            styles,
          } = props;

    const pickerRef = useRef<any>(null);
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedValues, setSelectedValues] = useState<any[]>(emptyArrayOnAllChecked ? [] : defaultSelectedValues ?? []);
    const [selectedValuesForTextBlock, setSelectedValuesForTextBlock] = useState<string>('');

    const _filteredList = (filteredList || []).map((el) => {
      if (typeof el === 'string') {
        return {
          id: el,
          name: el,
        }
      }

      return el;
    })

    useEffect(() => {
      setSelectedValuesForTextBlock(
        isAllSelected && displayAllInsteadOfElements
        ? 'Все'
        : _filteredList.filter((el) => (defaultSelectedValues || []).includes(el.id)).map(el => el.name).join(', ')
      );

      if (defaultSelectedValues === undefined) {
        setIsAllSelected(true);
      } else {
        setIsAllSelected(_filteredList.every((el) => (defaultSelectedValues || []).includes(el.id)));
      }
    }, [filteredList, defaultSelectedValues]);

    const onFilterChange = (vl: string) => {
      const selValues = getFilterList().filter(el => el.value.includes(vl)).map(el => el.data.id);

      unstable_batchedUpdates(() => {
        setSearchValue(vl);
        setSelectedValues(selValues.length < (_filteredList || []).length ? selValues : []);
        setIsAllSelected(!(selValues.length < (_filteredList || []).length));
      });
    };
    const onIsAllChecked = (ev: React.FormEvent<HTMLElement | HTMLInputElement> | undefined, vl?: boolean) => {
      unstable_batchedUpdates(() => {
        setSelectedValues([]);
        setIsAllSelected(vl || false);
      })
    };
    const getFilterList = (): any[] => {
      return _filteredList.map(filter => {
        return {
          key: filter.id,
          value: filter.name,
          data: filter,
        }
      })
    };
    const onElementChecked = (element: any, vl: any) => {
      if (isAllSelected && !vl) {
        const selectedValues = (_filteredList || [])
          .filter((el: any) => el.id !== element.id)
          .map((el: any) => el.id);

        unstable_batchedUpdates(() => {
          setSelectedValues(selectedValues);
          setIsAllSelected(false)
        });
      } else {
        const selValues = !vl
          ? selectedValues.filter((el: any) => el !== element.id)
          : [...selectedValues, element.id];
        
        if (selValues.length === (_filteredList || []).length) {
          unstable_batchedUpdates(() => {
            setSelectedValues(emptyArrayOnAllChecked ? [] : selValues);
            setIsAllSelected(true);
          });
        } else
          unstable_batchedUpdates(() => {
            setSelectedValues(selValues);
            setIsAllSelected(false)
          });
      }
    };

    const onFiltersAccept = () => {
      acceptFilter(field, isAllSelected && !emptyArrayOnAllChecked ? _filteredList.map(({ id }) => id) : selectedValues);
      onFilterClose();
      setSelectedValuesForTextBlock(
        isAllSelected && displayAllInsteadOfElements
        ? 'Все'
        : _filteredList.filter((el) => selectedValues.includes(el.id)).map(el => el.name).join(', ')
      );
      setSearchValue('');
    };

    useEffect(() => {
      return () => {
        setSearchValue('');
      }
    }, []);

    useEffect(() => {
      if (!isFiltered && !defaultSelectedValues) {
        unstable_batchedUpdates(() => {
          setSelectedValues([]);
          setIsAllSelected(true);
        });
      }
    }, [isFiltered, defaultSelectedValues]);

    const onClose = () => {
      setSearchValue('');

      if (defaultSelectedValues === undefined) {
        setIsAllSelected(true);
      } else {
        setIsAllSelected(_filteredList.every((el) => (defaultSelectedValues || []).includes(el.id)));
      }

      if (emptyArrayOnAllChecked) {
        setSelectedValues([]);
      } else {
        setSelectedValues(defaultSelectedValues ?? []);
      }

      onFilterClose();
    }

    return (
        <div ref={pickerRef}>
            {
              !useTextBlock &&
                <IconButton
                    iconProps={{ iconName: isFiltered && selectedValues.length ? 'FilterSolid' : 'Filter' }}
                    style={{color: theme.colors.themePrimary, cursor: 'pointer'}}
                    onClick={onShowFilter}
                />
            }
            {
              useTextBlock &&
              <TextField
                label={textBlockLabel}
                onClick={onShowFilter}
                value={selectedValuesForTextBlock}
                iconProps={{ iconName: 'ChevronDown' }}
                styles={{
                  fieldGroup: styles?.textInput,
                }}
              />
            }
            {showFilter && (
                <Callout
                  styles={{ root: { width: 300 } }}
                  target={pickerRef.current}
                  directionalHint={DirectionalHint.bottomRightEdge}
                  onDismiss={onClose}
                >
                  {
                    !hideSearch &&
                    <SearchBox
                      placeholder="Фильтр"
                      iconProps={{ iconName: 'Filter', styles: { root: { color: theme.colors.themePrimary } } }}
                      onChange={onFilterChange}
                    />
                  }
                  {isLoading && (
                    <LoaderContainer>
                      <Spinner label="Загрузка..." />
                    </LoaderContainer>
                  )}
                  {!isLoading &&
                    ((_filteredList || []).length > 0 ? (
                      <Stack
                        horizontal={false}
                        styles={{
                          root: { padding: '10px', minHeight: '100px' },
                        }}
                      >
                        <Checkbox
                          label="Все элементы"
                          checked={isAllSelected}
                          onChange={onIsAllChecked}
                        />
                        <Separator />
                        <FiltersList 
                          canLoadMore={canLoadMore} 
                          onLoadMore={onLoadMore} 
                          filtersList={getFilterList()} 
                          isAllSelected={isAllSelected} 
                          onElementChecked={onElementChecked} 
                          searchValue={searchValue} 
                          selectedValues={selectedValues} 
                        />
                      </Stack>
                    ) : (
                      <EmptyFilter>Ничего не найдено.</EmptyFilter>
                    ))}
                  <Stack
                    horizontal
                    horizontalAlign={'end'}
                    tokens={{ childrenGap: 10 }}
                  >
                    <PrimaryButton
                      text="Применить"
                      disabled={
                        canAcceptEmpty
                        ? false
                        : ((!isAllSelected &&
                            selectedValues.length === 0) ||
                           (_filteredList || []).length === 0)
                      }
                      onClick={onFiltersAccept}
                      data-qa='applyFilterBtn'
                    />
                    <DefaultButton text="Отмена" onClick={onClose} />
                  </Stack>
                </Callout>
            )}
        </div>
    );
}

export default FilterCallout;